import { fabric } from 'fabric';

import { isTextObject } from './fabric-setup';

/**
 * Parts of a graphic that belong together without being glued together.
 *
 * Studio draws a callout as four separate layers — words, the box around them,
 * a line, a dot — so each can be selected, moved and restyled on its own. A few
 * links keep them making sense while that happens:
 *   a box that wraps words (csGfxWrap) is refitted when the words change;
 *   a line that leaves a box or words (csGfxFrom) leaves from its nearest side;
 *   a line that ends on a dot (csGfxTo) follows the dot.
 * Each part carries its own id (csGfxId); the links are ids, serialised with
 * the scene, so they survive save and reopen.
 */

type Box = { left: number; top: number; width: number; height: number };

const idOf = (o: any): string | undefined => o?.csGfxId;

/** Where a line leaves a box, heading for a point: the facing side, or straight up/down when the point is above/below it. */
export function anchorOnBox(b: Box, p: { x: number; y: number }): { x: number; y: number } {
    const right = b.left + b.width;
    const bottom = b.top + b.height;
    if (p.x >= b.left && p.x <= right) return { x: p.x, y: p.y < b.top ? b.top : bottom };
    return { x: p.x > right ? right : b.left, y: b.top + b.height / 2 };
}

/** An object's box on the canvas, as drawn (position and scale; not rotation). */
function boxOf(o: fabric.Object): Box {
    const r = o.getBoundingRect(true, true);
    return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/** A line's two ends on the canvas, wherever it has been moved or scaled to. */
function lineEnds(l: fabric.Line): { a: fabric.Point; b: fabric.Point } {
    const pts = (l as any).calcLinePoints();
    const m = l.calcTransformMatrix();
    return {
        a: fabric.util.transformPoint(new fabric.Point(pts.x1, pts.y1), m),
        b: fabric.util.transformPoint(new fabric.Point(pts.x2, pts.y2), m),
    };
}

/** Fit a wrapping box around its words, keeping the padding it was drawn with. */
function refitBox(rect: fabric.Rect, words: fabric.Object): void {
    const padX = Number((rect as any).csGfxPadX) || 0;
    const padY = Number((rect as any).csGfxPadY) || 0;
    const b = boxOf(words);
    const sw = rect.strokeWidth || 0;
    rect.set({
        left: b.left - padX - sw / 2,
        top: b.top - padY - sw / 2,
        width: (b.width + padX * 2) / (rect.scaleX || 1),
        height: (b.height + padY * 2) / (rect.scaleY || 1),
        angle: 0,
    });
    rect.setCoords();
}

/** Re-run a line from its source's nearest side to its target. */
function reanchorLine(c: fabric.Canvas, line: fabric.Line): void {
    const objs = c.getObjects();
    const from = (line as any).csGfxFrom ? objs.find((o) => idOf(o) === (line as any).csGfxFrom) : undefined;
    const to = (line as any).csGfxTo ? objs.find((o) => idOf(o) === (line as any).csGfxTo) : undefined;
    const ends = lineEnds(line);
    const end = to ? to.getCenterPoint() : ends.b;
    const start = from ? anchorOnBox(boxOf(from), end) : ends.a;
    line.set({ scaleX: 1, scaleY: 1, angle: 0 } as any);
    line.set({ x1: start.x, y1: start.y, x2: end.x, y2: end.y } as any);
    line.setCoords();
}

/**
 * Something linked changed (words edited, a part moved): bring its partners
 * along. Returns true when anything was adjusted.
 */
export function relinkGraphics(c: fabric.Canvas, changed: fabric.Object | null | undefined): boolean {
    // Several parts moved together move as a whole already; and inside a
    // selection their coordinates are the selection's, not the canvas's.
    if (!changed || changed.type === 'activeSelection' || changed.group) return false;
    const moved = [changed];
    const ids = new Set(moved.map(idOf).filter(Boolean) as string[]);
    if (!ids.size) return false;
    const objs = c.getObjects();
    let touched = false;
    // Words changed or moved: their boxes follow…
    for (const o of objs) {
        const wrap = (o as any).csGfxWrap;
        if (o.type === 'rect' && wrap && ids.has(wrap) && !moved.includes(o)) {
            const words = objs.find((x) => idOf(x) === wrap && isTextObject(x));
            if (words) { refitBox(o as fabric.Rect, words); if (idOf(o)) ids.add(idOf(o)!); touched = true; }
        }
    }
    // …and every line leaving or reaching a part that moved is re-run.
    for (const o of objs) {
        if (o.type !== 'line' || moved.includes(o)) continue;
        const f = (o as any).csGfxFrom;
        const t = (o as any).csGfxTo;
        if ((f && ids.has(f)) || (t && ids.has(t))) { reanchorLine(c, o as fabric.Line); touched = true; }
    }
    if (touched) c.requestRenderAll();
    return touched;
}
